import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useStripeContext } from '../../contexts/StripeContext';
import { paymentService } from '../../services/payments';
import Button from '../ui/Button';
import AppIcon from '../AppIcon';

const BillingSetupFormInner = ({ clientSecret, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);

  React.useEffect(() => {
    if (elements) {
      const paymentElement = elements?.getElement(PaymentElement);
      if (paymentElement) {
        paymentElement?.on('ready', () => {
          setPaymentReady(true);
        });
      }
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setErrorMessage('Payment system not ready. Please try again.');
      setErrorType('system');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');
    setErrorType(null);

    try {
      const { error: stripeError, setupIntent } = await stripe?.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/agent-account-settings`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        // Handle specific error types
        if (stripeError?.code === 'setup_intent_authentication_failure') {
          setErrorType('authentication');
          setErrorMessage(
            'We were unable to authenticate your payment method. This usually happens when 3D Secure verification fails. Please try a different card or contact your bank.'
          );
        } else if (stripeError?.code === 'card_declined') {
          setErrorType('declined');
          setErrorMessage('Your card was declined. Please try a different payment method.');
        } else if (stripeError?.type === 'validation_error') {
          setErrorType('validation');
          setErrorMessage(stripeError?.message || 'Please check your card details and try again.');
        } else {
          setErrorType('general');
          setErrorMessage(stripeError?.message || 'Failed to save payment method. Please try again.');
        }
        onError?.(stripeError);
      } else if (setupIntent) {
        if (setupIntent?.status === 'succeeded') {
          onSuccess?.(setupIntent);
        } else {
          setErrorType('incomplete');
          setErrorMessage('Payment method setup incomplete. Please try again.');
        }
      }
    } catch (error) {
      console.error('Setup error:', error);
      setErrorType('system');
      setErrorMessage('An unexpected error occurred. Please try again.');
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!stripe || !elements || !clientSecret) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
        <p className="text-sm text-gray-500 text-center">Loading payment system...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <label className="text-sm font-medium text-gray-900">
          Payment Method
        </label>
        <PaymentElement 
          options={{
            fields: {
              billingDetails: 'auto'
            },
            layout: 'tabs'
          }}
        />
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
          <div className="flex items-start space-x-3">
            <AppIcon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-900 text-sm font-medium">
                {errorType === 'authentication' && 'Authentication Failed'}
                {errorType === 'declined' && 'Card Declined'}
                {errorType === 'validation' && 'Invalid Card Details'}
                {errorType === 'incomplete' && 'Setup Incomplete'}
                {(errorType === 'general' || errorType === 'system') && 'Payment Error'}
              </p>
              <p className="text-red-800 text-sm mt-1">{errorMessage}</p>
            </div>
          </div>
          {errorType === 'authentication' && (
            <div className="bg-red-100 rounded p-3 text-xs text-red-900">
              <p className="font-medium mb-1">Troubleshooting tips:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Try a different card that supports 3D Secure authentication</li>
                <li>Contact your bank to ensure online payments are enabled</li>
                <li>Check if your card is enrolled for 3D Secure/Verified by Visa</li>
              </ul>
            </div>
          )}
        </div>
      )}

      <Button
        type="submit"
        disabled={!paymentReady || isProcessing || !stripe || !elements}
        className="w-full"
        iconName={isProcessing ? "Loader2" : "CreditCard"}
        iconClassName={isProcessing ? "animate-spin" : ""}
        iconPosition="left"
      >
        {isProcessing ? 'Saving Payment Method...' : 'Save Payment Method'}
      </Button>

      <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
        <AppIcon name="Shield" size={14} />
        <span>Secured by Stripe • You'll only be charged when you accept an enquiry</span>
      </div>
    </form>
  );
};

const BillingSetupModal = ({ isOpen, onClose, agentId, onSuccess }) => {
  const { stripePromise, stripeOptions } = useStripeContext();
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (isOpen && agentId) {
      initializeSetup();
    }
  }, [isOpen, agentId]);

  const initializeSetup = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const setupData = await paymentService?.createSetupIntent(agentId);
      if (setupData?.clientSecret) {
        setClientSecret(setupData?.clientSecret);
      } else {
        throw new Error('Failed to initialize payment setup');
      }
    } catch (err) {
      console.error('Setup initialization error:', err);
      setError(err?.message || 'Failed to initialize payment setup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccess = (setupIntent) => {
    console.log('Payment method saved successfully:', setupIntent);
    // Call onSuccess immediately with setupIntent data
    onSuccess?.(setupIntent);
    // Longer delay before closing to allow webhook processing and state update
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  const handleError = (error) => {
    console.error('Payment method setup error:', error);
    setError(error?.message || 'Failed to save payment method.');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add Payment Method</h2>
            <p className="text-sm text-gray-600 mt-1">
              Save your card for quick enquiry acceptance
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <AppIcon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
            <AppIcon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-blue-900 font-medium">Card on File</p>
              <p className="text-sm text-blue-800 mt-1">
                Your card will be securely saved. You'll only be charged £7.00 when you accept an enquiry.
              </p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AppIcon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 text-sm font-medium">Error</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="ml-4 text-gray-600">Initializing secure payment setup...</p>
            </div>
          )}

          {/* Payment Form */}
          {!isLoading && clientSecret && (
            <Elements stripe={stripePromise} options={{
              clientSecret,
              ...stripeOptions
            }}>
              <BillingSetupFormInner
                clientSecret={clientSecret}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            </Elements>
          )}

          {/* Retry Button */}
          {!isLoading && !clientSecret && error && (
            <Button
              onClick={initializeSetup}
              className="w-full"
              iconName="RefreshCw"
              iconPosition="left"
            >
              Retry Setup
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingSetupModal;